import { z } from 'zod';
// Domain restriction — single source of truth for frontend and backend.
// Override in the backend with env ALLOWED_EMAIL_DOMAINS (comma-separated);
// the browser build always uses the default list below.
export const DEFAULT_ALLOWED_EMAIL_DOMAINS = [
    'casagrand.co.in',
    'casagrandcontracts.com',
    'digilogy.co',
    'casagrandtravelogy.co.in',
];
export const ALLOWED_EMAIL_DOMAINS = typeof process !== 'undefined' && process.env?.ALLOWED_EMAIL_DOMAINS
    ? process.env.ALLOWED_EMAIL_DOMAINS.split(',')
        .map((d) => d.trim().toLowerCase())
        .filter(Boolean)
    : DEFAULT_ALLOWED_EMAIL_DOMAINS;
/** @deprecated kept for backward compatibility — use ALLOWED_EMAIL_DOMAINS */
export const ALLOWED_EMAIL_DOMAIN = ALLOWED_EMAIL_DOMAINS[0];
export function isValidEmailDomain(email) {
    const lower = email.toLowerCase();
    return ALLOWED_EMAIL_DOMAINS.some((domain) => lower.endsWith(`@${domain}`));
}
// Email Auth Schemas
export const emailLoginSchema = z.object({
    email: z
        .string()
        .email('Please enter a valid email address')
        .refine((val) => isValidEmailDomain(val), {
        message: `Authentication is restricted to: ${ALLOWED_EMAIL_DOMAINS.join(', ')}`,
    }),
});
export const otpVerifySchema = z.object({
    email: z
        .string()
        .email()
        .refine((val) => isValidEmailDomain(val)),
    otp: z.string().length(6, 'OTP must be exactly 6 digits'),
});
// Idea Submission Zod Schemas
export const DEPARTMENTS = [
    'Operations',
    'Legal',
    'Engineering Management',
    'Chairman Office',
    'P&L',
    'Administration',
    'Engineering Execution',
    'Finance',
    'SAP',
    'Purchase',
    'Engineering Development',
    'Sales',
    'Management',
    'Engineering Execution Unit III',
    'Customer Interface Team',
    'Liaison',
    'MIS',
    'Customer Relationship Management',
    'Survey',
    'Land Acquisition',
    'Telemarketing',
    'Stores',
    'Marketing',
    'Customization & Modification',
    'Product Development',
    'Internal Audit',
    'QA/QC',
    'SARA',
    'Customer Banking Service',
    'Customer Delight',
    'Safety',
    'IT',
    'Human Resources',
    'Management Administration',
    'Process & Compliance',
    'Project Give And Take',
    'Issues',
    'Projects',
    'Integrated Facilities Management',
    'Engineering Commercial',
    'Planning',
    'Management Engineering',
    'External Contracts',
    'Liaison Secondary',
    'Precast',
    'Engineering',
    'Engineering Execution Unit IV',
    'Rentassure',
    'Business Development',
    'Projects B',
    'Finance & Operations',
    'Contracts',
    'Information Technology',
    'Funding',
    'Loss Control',
];
export const CATEGORIES = [
    'Cost Saving',
    'Revenue',
    'Time Saving',
    'Productivity',
    'Safety & Compliance',
    'Sustainability',
    'Tech & Automation',
];
export const submitIdeaSchema = z.object({
    title: z.string().min(5, 'Name of idea must be at least 5 characters').max(150, 'Name of idea cannot exceed 150 characters'),
    description: z.string().min(20, 'Description must be at least 20 characters'),
    department: z.string().min(2, 'Department is required'),
    category: z.string().min(2, 'Category is required'),
    expectedBenefits: z.string().min(10, 'Expected benefits must be at least 10 characters'),
    // Optional Fields
    costSaving: z.coerce.number().nonnegative().max(9999999999.99, 'Indicative saving amount is too large').optional().nullable(),
    revenue: z.coerce.number().nonnegative().max(9999999999.99, 'Amount too large').optional().nullable(),
    timeSaving: z.string().optional().nullable(),
    productivity: z.string().optional().nullable(),
    // Attachments references uploaded in storage
    attachments: z.array(z.object({
        fileName: z.string(),
        fileKey: z.string(),
        fileSize: z.number().max(50 * 1024 * 1024, 'File size cannot exceed 50MB'),
        fileType: z.string(),
    })).optional().default([]),
});
// Admin Review schemas
export const adminReviewSchema = z.object({
    status: z.enum(['SUBMITTED', 'APPROVED', 'REJECTED', 'MORE_INFO_REQUESTED', 'UNDER_REVIEW', 'PENDING_HEAD_APPROVAL', 'PENDING_EXTERNAL_APPROVAL', 'DUPLICATE', 'WORK_IN_PROGRESS']),
    comment: z.string().min(5, 'A comment is required for review changes (min 5 characters)'),
    validation: z.object({
        currentCost: z.string().optional(),
        proposeSolution: z.string().optional(),
        savingType: z.string().optional(),
        annualSavings: z.string().optional(),
        oneTimeInvestment: z.string().optional(),
        owner: z.string().optional(),
        implementationTimeline: z.string().optional(),
        risk: z.string().optional(),
        notes: z.string().optional(),
        attachments: z.array(z.object({
            fileName: z.string(),
            fileKey: z.string(),
            fileSize: z.number(),
            fileType: z.string(),
        })).optional(),
        updatedFieldsStr: z.string().optional(),
    }).optional(),
});
export const adminAssignSchema = z.object({
    validationTeam: z.string().optional().nullable(),
    executionTeam: z.string().optional().nullable(),
    priority: z.enum(['HIGH', 'MEDIUM', 'LOW']).optional().nullable(),
});
export const committeeAssignSchema = z.object({
    assigneeIds: z.array(z.string().uuid()),
});
export const updateEmailConfirmationSchema = z.object({
    approvedAmount: z.string().optional().nullable(),
    reason: z.string().optional().nullable(),
    confirmationDate: z.string().optional().nullable(),
    attachments: z.array(z.object({
        fileName: z.string(),
        fileKey: z.string(),
        fileSize: z.number(),
        fileType: z.string(),
    })).optional().default([]),
});
export const addCommentSchema = z.object({
    content: z.string().min(1, 'Comment cannot be empty'),
});
export const evaluateIdeaSchema = z.object({
    savingPotential: z.boolean(),
    easeOfImplementation: z.boolean(),
    timeOfImplementation: z.boolean(),
    category: z.enum(['A', 'B', 'C']),
    comments: z.string().optional().nullable(),
    updatedFieldsStr: z.string().optional(),
    attachments: z.array(z.object({
        fileName: z.string(),
        fileKey: z.string(),
        fileSize: z.number(),
        fileType: z.string(),
    })).optional(),
});
//# sourceMappingURL=index.js.map